"use client";

import { useEffect } from 'react';
import { useBranding } from "@/app/_module/store/contexts/brandingContext";

/**
 * Strict mobile detection - only returns true for actual mobile devices
 * Excludes desktop browsers even if they have touch support
 */
function isStrictlyMobile(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const platform = navigator.platform || '';
  
  // Check for desktop browsers explicitly - exclude these
  const isDesktop = /Windows|Win32|Win64|Macintosh|MacIntel|Linux|X11/i.test(platform) && 
                    !/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  
  if (isDesktop) {
    return false;
  }
  
  // Check for iOS devices (iPhone, iPad, iPod) - but exclude desktop Safari
  const isiOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  
  // Check for Android devices
  const isAndroid = /android/i.test(userAgent);
  
  // Check for other mobile devices
  const isOtherMobile = /Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  
  // iPad detection - but exclude desktop Macs with touch
  // Real iPad has "iPad" in userAgent OR (MacIntel with touch but no mouse)
  const isRealIpad = /iPad/.test(userAgent) || 
    (platform === 'MacIntel' && navigator.maxTouchPoints > 1 && 
     !window.matchMedia('(pointer: fine)').matches);
  
  // Only return true for actual mobile devices
  return (isiOS || isAndroid || isOtherMobile || isRealIpad) && !isDesktop;
}

/**
 * Client component that conditionally adds PWA meta tags
 * only when a mobile device is detected
 */
export function MobilePWAMeta() {
  const { branding } = useBranding();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Always remove existing PWA meta tags first (ensures desktop is clean)
    const existingTags = document.querySelectorAll(
      'meta[name="apple-mobile-web-app-capable"], ' +
      'meta[name="mobile-web-app-capable"], ' +
      'meta[name="apple-mobile-web-app-status-bar-style"], ' +
      'meta[name="apple-mobile-web-app-title"], ' +
      'link[rel="manifest"], ' +
      'link[rel="apple-touch-icon"]'
    );
    existingTags.forEach(tag => tag.remove());

    // Only add PWA tags if we're strictly on mobile
    const isMobile = isStrictlyMobile();

    if (!isMobile) {
      return;
    }

    // Add PWA meta tags only on mobile
    const appleCapable = document.createElement('meta');
    appleCapable.name = 'apple-mobile-web-app-capable';
    appleCapable.content = 'yes';
    document.head.appendChild(appleCapable);

    const mobileCapable = document.createElement('meta');
    mobileCapable.name = 'mobile-web-app-capable';
    mobileCapable.content = 'yes';
    document.head.appendChild(mobileCapable);

    const statusBar = document.createElement('meta');
    statusBar.name = 'apple-mobile-web-app-status-bar-style';
    statusBar.content = 'default';
    document.head.appendChild(statusBar);

    const appTitle = document.createElement('meta');
    appTitle.name = 'apple-mobile-web-app-title';
    appTitle.content = branding.brandName;
    document.head.appendChild(appTitle);

    // Add manifest link only on mobile
    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    manifestLink.href = '/site.webmanifest';
    document.head.appendChild(manifestLink);

    // Add explicit apple-touch-icon link for Safari PWA
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = branding.faviconUrl || '/favicon.png';
    appleTouchIcon.sizes = '180x180';
    document.head.appendChild(appleTouchIcon);

    // Cleanup function
    return () => {
      appleCapable.remove();
      mobileCapable.remove();
      statusBar.remove();
      appTitle.remove();
      manifestLink.remove();
      appleTouchIcon.remove();
    };
  }, [branding.brandName, branding.faviconUrl]);

  return null;
}
